import React, { useEffect, useState } from 'react';
import { AlertTriangle, ArrowLeftRight } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useToast } from '../contexts/ToastContext';
import { ucCodigoKeys } from '../lib/ucCodigo';
import type { UcExistente } from '../lib/ucDuplicada';
import {
  buscarTrocasDaNegociacao,
  cancelarTrocaDonoUc,
  descreverTroca,
  dividaDoDonoAnterior,
  type DividaDonoAnterior,
  type TrocaDonoUc,
} from '../lib/trocaDonoUc';

/**
 * Avisos da troca de dono da UC (venda do estabelecimento) — src/lib/trocaDonoUc.ts.
 *
 * Dois lugares, dois recados:
 *  - na negociação: em que pé está a troca, dos dois lados (quem comprou e quem vendeu);
 *  - na análise da negociação nova: de onde a UC vem e quanto o dono anterior
 *    deixou em aberto. Não trava nada — a dívida fica com ele —, mas é o que
 *    denuncia "venda" para parente só para limpar o nome.
 */

const formatarReais = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const formatarData = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('pt-BR') : '');

const useTrocasDaNegociacao = (negociacaoId: number) => {
  const [trocas, setTrocas] = useState<TrocaDonoUc[]>([]);
  const [versao, setVersao] = useState(0);
  useEffect(() => {
    let vivo = true;
    buscarTrocasDaNegociacao(supabase, negociacaoId)
      .then(lista => { if (vivo) setTrocas(lista); })
      // Sem a tabela (banco ainda sem a migration) ou sem permissão: sem aviso.
      .catch(err => console.error('Troca de dono: não foi possível ler os pedidos', err));
    return () => { vivo = false; };
  }, [negociacaoId, versao]);
  return { trocas, recarregar: () => setVersao(v => v + 1) };
};

const CaixaAviso: React.FC<{ titulo: string; children: React.ReactNode }> = ({ titulo, children }) => (
  <div className="mb-4 p-3 rounded-xl border border-amber-200 dark:border-amber-800/40 bg-amber-50 dark:bg-amber-900/10 text-sm">
    <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-amber-800 dark:text-amber-300 mb-1.5">
      <ArrowLeftRight size={14} /> {titulo}
    </p>
    <div className="flex flex-col gap-2 text-amber-900 dark:text-amber-200">{children}</div>
  </div>
);

const CancelarPedido: React.FC<{ troca: TrocaDonoUc; onCancelado: () => void }> = ({ troca, onCancelado }) => {
  const { addToast } = useToast();
  const [aberto, setAberto] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [enviando, setEnviando] = useState(false);

  const confirmar = async () => {
    if (motivo.trim().length < 5) {
      addToast('Diga em poucas palavras por que a troca não vai acontecer.', 'error');
      return;
    }
    setEnviando(true);
    try {
      await cancelarTrocaDonoUc(supabase, troca.id, motivo.trim());
      addToast(`Pedido cancelado. A UC ${troca.numero_uc} voltou para a negociação anterior.`, 'success');
      onCancelado();
    } catch (err: any) {
      addToast(err?.message || 'Não foi possível cancelar o pedido.', 'error');
    } finally {
      setEnviando(false);
    }
  };

  if (!aberto) {
    return (
      <button
        type="button"
        onClick={() => setAberto(true)}
        className="self-start text-[11px] font-semibold text-amber-700 dark:text-amber-400 underline underline-offset-2 hover:text-amber-900"
      >
        Cancelar pedido de troca
      </button>
    );
  }
  return (
    <div className="flex flex-col gap-1.5">
      <input
        value={motivo}
        onChange={e => setMotivo(e.target.value)}
        placeholder="Motivo (ex.: a venda não saiu)"
        className="w-full border border-amber-200 dark:border-amber-800/50 rounded-lg px-2 py-1.5 text-xs bg-white dark:bg-slate-900 text-gray-800 dark:text-slate-100 outline-none focus:border-amber-400"
      />
      <div className="flex gap-2 justify-end">
        <button type="button" onClick={() => setAberto(false)} className="px-2 py-1 text-[11px] font-semibold rounded-md border border-gray-200 dark:border-slate-700 text-gray-600 dark:text-slate-300">
          Voltar
        </button>
        <button type="button" disabled={enviando} onClick={confirmar} className="px-2 py-1 text-[11px] font-bold rounded-md bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-50">
          {enviando ? 'Cancelando...' : 'Confirmar cancelamento'}
        </button>
      </div>
    </div>
  );
};

/** Na negociação (CRM): estado da troca, do lado de quem comprou e de quem vendeu. */
export const TrocaDonoNegociacaoAviso: React.FC<{ negociacaoId: number; onChange?: () => void }> = ({ negociacaoId, onChange }) => {
  const { trocas, recarregar } = useTrocasDaNegociacao(negociacaoId);
  const visiveis = trocas.filter(t => t.status === 'pendente' || t.status === 'efetivada');
  if (visiveis.length === 0) return null;

  return (
    <CaixaAviso titulo="Troca de dono da UC">
      {visiveis.map(t => {
        const lado = t.negociacao_destino_id === negociacaoId ? 'novo' : 'antigo';
        return (
          <div key={t.id} className="flex flex-col gap-1">
            <p className="text-xs leading-relaxed">{descreverTroca(t, lado)}</p>
            <p className="text-[11px] text-amber-700/80 dark:text-amber-400/80">
              Pedido por {t.solicitado_por_nome || '—'} em {formatarData(t.created_at)}
              {t.status === 'efetivada' && t.efetivada_em ? ` · troca feita em ${formatarData(t.efetivada_em)}` : ''}
              {t.status === 'pendente' ? ` · vale até ${formatarData(t.expira_em)}` : ''}
              {' · '}"{t.justificativa}"
            </p>
            {t.status === 'pendente' && t.erro && (
              <p className="flex items-start gap-1 text-[11px] font-semibold text-red-700 dark:text-red-400">
                <AlertTriangle size={12} className="shrink-0 mt-0.5" />
                A troca não foi concluída sozinha: {t.erro} O sistema tenta de novo todo dia às 7h15.
              </p>
            )}
            {t.status === 'pendente' && lado === 'novo' && (
              <CancelarPedido troca={t} onCancelado={() => { recarregar(); onChange?.(); }} />
            )}
          </div>
        );
      })}
    </CaixaAviso>
  );
};

const CAMPOS_STATUS = 'status_fatura, status_boleto_vigor, valor_boleto_vigor, valor_emissao_boleto_unico, vencimento_boleto, data_vencimento, data_pgto_boleto, quitado_em, energia_injetada_gd1_kwh, energia_injetada_gd2_kwh';

/** Dívida em aberto nas faturas da UC que ainda são do dono anterior. */
async function buscarDividaDoDonoAnterior(troca: TrocaDonoUc): Promise<DividaDonoAnterior> {
  const { data: uc } = await supabase
    .from('unidades_consumidoras')
    .select('numero_uc, outro_numero_uc')
    .eq('id', troca.uc_id)
    .maybeSingle();
  const chaves = ucCodigoKeys(uc?.numero_uc ?? troca.numero_uc, uc?.outro_numero_uc);
  if (chaves.length === 0) return { valor: 0, faturas: 0, vencidas: 0 };
  let q = supabase
    .from('faturas')
    .select(CAMPOS_STATUS)
    .in('uc_codigo_norm', chaves)
    .eq('ambiente', 'producao')
    .is('excluido_em', null);
  // Troca feita: as dele são as carimbadas. Pedido aberto: todas ainda são dele.
  q = troca.status === 'efetivada' ? q.eq('dono_anterior_troca_id', troca.id) : q.is('dono_anterior_troca_id', null);
  const { data, error } = await q;
  if (error) throw error;
  return dividaDoDonoAnterior(data ?? []);
}

/** Na análise cadastral da negociação nova: de onde a UC vem e a dívida do dono anterior. */
export const TrocaDonoAnaliseAviso: React.FC<{ negociacaoId: number }> = ({ negociacaoId }) => {
  const { trocas } = useTrocasDaNegociacao(negociacaoId);
  const daqui = trocas.filter(t => t.negociacao_destino_id === negociacaoId && t.status === 'pendente');
  const [dividas, setDividas] = useState<Record<string, DividaDonoAnterior | null>>({});
  const chave = daqui.map(t => t.id).join(',');

  useEffect(() => {
    if (!chave) return;
    let vivo = true;
    Promise.all(daqui.map(t => buscarDividaDoDonoAnterior(t).then(d => [t.id, d] as const).catch(() => [t.id, null] as const)))
      .then(pares => { if (vivo) setDividas(Object.fromEntries(pares)); });
    return () => { vivo = false; };
    // `chave` resume `daqui`: refaz só quando muda o conjunto de pedidos.
  }, [chave]);

  if (daqui.length === 0) return null;

  return (
    <CaixaAviso titulo="Troca de dono — confira antes de aprovar">
      {daqui.map(t => {
        const antigo = t.dono_anterior?.nome_cliente?.trim() || 'o dono anterior';
        const divida = dividas[t.id];
        return (
          <div key={t.id} className="flex flex-col gap-1 text-xs leading-relaxed">
            <p>
              A UC <strong>{t.numero_uc}</strong> vem de <strong>{antigo}</strong>
              {t.negociacao_origem_id ? ` (negociação #${t.negociacao_origem_id})` : ''}, que vendeu o estabelecimento.
              Pedido por {t.solicitado_por_nome || '—'}: "{t.justificativa}".
            </p>
            {divida === undefined ? (
              <p className="text-amber-700/80 dark:text-amber-400/80">Conferindo as faturas em aberto de {antigo}...</p>
            ) : divida === null ? (
              <p className="text-amber-700/80 dark:text-amber-400/80">Não foi possível conferir a dívida de {antigo}.</p>
            ) : divida.faturas === 0 ? (
              <p>{antigo} não deixou fatura em aberto nesta UC.</p>
            ) : (
              <p className="font-semibold">
                {antigo} deixou {formatarReais(divida.valor)} em aberto ({divida.faturas} {divida.faturas === 1 ? 'fatura' : 'faturas'}
                {divida.vencidas > 0 ? `, ${divida.vencidas} vencida${divida.vencidas === 1 ? '' : 's'}` : ''}).
                A dívida continua com o dono anterior e não trava esta análise. Se este cliente for da mesma família ou empresa, avalie se a venda é de verdade.
              </p>
            )}
            <p className="text-amber-700/80 dark:text-amber-400/80">
              Ao aprovar esta análise, a UC passa para este cliente: a próxima fatura sai no nome dele e as anteriores continuam com {antigo}.
            </p>
          </div>
        );
      })}
    </CaixaAviso>
  );
};

/**
 * Aviso de UC já cadastrada, com a saída para a venda do estabelecimento: em vez
 * de duplicar a UC (a trava do banco não deixa, e o robô de fatura não saberia de
 * quem é a conta), o vendedor pede a troca de dono dela.
 */
export const AvisoUcExistente: React.FC<{
    uc: UcExistente;
    pedindo: boolean;
    justificativa: string;
    onPedir: () => void;
    onJustificativa: (v: string) => void;
    onConfirmar: () => void;
    onCancelar: () => void;
}> = ({ uc, pedindo, justificativa, onPedir, onJustificativa, onConfirmar, onCancelar }) => {
    const numeros = [uc.numero_uc, uc.outro_numero_uc].filter(Boolean).join(' / ');
    const dono = uc.nome_cliente?.trim() || 'outro cliente';
    return (
        <div className="mb-4 p-3 rounded-xl border border-amber-200 dark:border-amber-800/40 bg-amber-50 dark:bg-amber-900/10 text-sm">
            <div className="flex items-start gap-2 text-amber-800 dark:text-amber-300">
                <AlertTriangle size={18} className="shrink-0 mt-0.5" />
                <p>
                    A UC <span className="font-bold">{numeros}</span> já está cadastrada no nome de <span className="font-bold">{dono}</span>.
                    Não dá para cadastrar a mesma UC duas vezes.
                </p>
            </div>
            {uc.empresa_parceira_id ? (
                <p className="mt-2 text-xs text-amber-700 dark:text-amber-400">
                    É UC de empresa parceira: se o estabelecimento foi vendido, peça a troca de dono ao Financeiro.
                </p>
            ) : !pedindo ? (
                <button
                    type="button"
                    onClick={onPedir}
                    className="mt-3 flex items-center gap-2 px-3 py-2 text-xs font-bold rounded-lg bg-white dark:bg-slate-900 border border-amber-300 dark:border-amber-700 text-amber-800 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors"
                >
                    <ArrowLeftRight size={14} /> O estabelecimento foi vendido? Pedir troca de dono
                </button>
            ) : (
                <div className="mt-3 flex flex-col gap-2">
                    <label className="block text-xs font-bold text-gray-700 dark:text-slate-300 uppercase">
                        Justificativa <span className="text-red-500">*</span>
                    </label>
                    <textarea
                        value={justificativa}
                        onChange={e => onJustificativa(e.target.value)}
                        rows={3}
                        autoFocus
                        className="w-full border border-gray-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm focus:border-primary focus:ring-1 focus:ring-primary/20 outline-none bg-white dark:bg-slate-900 text-gray-800 dark:text-slate-100 placeholder:text-gray-400 dark:placeholder:text-slate-500 resize-y"
                        placeholder={`Ex.: ${dono} vendeu o estabelecimento para este cliente em setembro, e ele vai continuar na Vigor.`}
                    />
                    <ul className="text-[11px] text-gray-600 dark:text-slate-400 list-disc pl-4 space-y-0.5">
                        <li>A UC entra nesta negociação, mas continua sendo cobrada de {dono} até a análise daqui ser aprovada.</li>
                        <li>As faturas e dívidas de {dono} continuam com o dono atual. Este cliente começa do zero.</li>
                        <li>O vendedor de {dono} e os gerentes são avisados. O cliente antigo não.</li>
                        <li>Se a análise não for aprovada em 30 dias, a UC volta para {dono}.</li>
                    </ul>
                    <div className="flex justify-end gap-2">
                        <button
                            type="button"
                            onClick={onCancelar}
                            className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-gray-200 dark:border-slate-700 text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-800"
                        >
                            Cancelar
                        </button>
                        <button
                            type="button"
                            onClick={onConfirmar}
                            className="px-3 py-1.5 text-xs font-bold rounded-lg bg-primary text-white hover:bg-primary/90"
                        >
                            Adicionar como troca de dono
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};
